import React, { useEffect, useState } from "react";

// mui components
import {
	Autocomplete,
	Box,
	Stack,
	TableCell,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
} from "@mui/material";

// mui utils
import { visuallyHidden } from "@mui/utils";

// custom components
import OutlinedTextInput from "../../custom-input/outlined-text-input";
import Heading4 from "../../typography/heading-04";

// date picker
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// custom styles
import styles from "./index.module.scss";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const mapStatusForApi = (value = "") => {
	if (!value) return "";
	return value === "Uploaded / Pending Submission" ? "Uploaded" : value;
};

const TableHeadUploadHistory = (props) => {
	const {
		data,
		// sort
		order,
		orderBy,
		onRequestSort,
		filters,
		onFiltersChange,
	} = props;

	const sortableIds = new Set([
		"borrower_name",
		"loan_application_date",
		"loan_application_completed_date",
		"status",
		"action",
	]);

	const statusOptions = [
		"Uploaded / Pending Submission",
		"Pending Application Submission",
		"Loan Submitted",
	];

	const createSortHandler = (property) => () => {
		onRequestSort(property);
	};

	const [statusInput, setStatusInput] = useState(filters?.status || "");

	useEffect(() => {
		if (!filters?.status) {
			setStatusInput("");
		}
	}, [filters?.status]);

	return (
		<TableHead>
			<TableRow>
				{data.map((headCell) => (
					<TableCell
						key={headCell.id}
						align={headCell.numeric ? "right" : "left"}
						padding={headCell.disablePadding ? "none" : "normal"}
						sortDirection={
							sortableIds.has(headCell.id) && orderBy === headCell.id ? order : false
						}
						style={{ minWidth: headCell.minWidth }}
					>
						<Stack direction="column" className={styles.headerStack}>
							{sortableIds.has(headCell.id) ? (
								<TableSortLabel
									active={orderBy === headCell.id}
									direction={orderBy === headCell.id ? order : "asc"}
									onClick={createSortHandler(headCell.id)}
									className={styles.tableLabel}
								>
									<Stack direction="row" alignItems="center" gap={0.5}>
										<Heading4
											text={headCell.label}
											extraClass={styles.uploadHistoryTh}
											fontType="semibold"
										/>
										{headCell.label === "Go To Loan" && (
											<ArrowForwardIcon sx={{ fontSize: "18px" }} />
										)}
									</Stack>
									{orderBy === headCell.id ? (
										<Box component="span" sx={visuallyHidden}>
											{order === "desc"
												? "sorted descending"
												: "sorted ascending"}
										</Box>
									) : null}
								</TableSortLabel>
							) : (
								<Stack direction="row" alignItems="center" gap={0.5}>
									<Heading4
										text={headCell.label}
										extraClass={styles.uploadHistoryTh}
										fontType="semibold"
									/>
									{headCell.label === "Go To Loan" && (
										<ArrowForwardIcon sx={{ fontSize: "18px" }} />
									)}
								</Stack>
							)}
							{headCell.label === "Status" ? (
								<Autocomplete
									sx={{
										"& .MuiOutlinedInput-root": {
											paddingTop: "0px",
											paddingLeft: "0px",
											paddingBottom: "0px",
										},
									}}
									id="status-filter"
									className={styles.inputField}
									options={statusOptions}
									freeSolo
									value={statusInput}
									inputValue={statusInput}
									onInputChange={(event, newValue) => {
										setStatusInput(newValue);
										onFiltersChange?.("status", mapStatusForApi(newValue));
									}}
									onChange={(event, newValue) => {
										setStatusInput(newValue || "");
										onFiltersChange?.("status", mapStatusForApi(newValue || ""));
									}}
									getOptionLabel={(option) => option || ""}
									style={{ width: 300, display: "flex", alignItems: "center" }}
									renderInput={(params) => (
										<TextField
											{...params}
											sx={{
												"& .MuiOutlinedInput-notchedOutline": {
													borderWidth: "1px !important",
													borderColor: "#000 !important",
													borderRadius: "2px",
												},
											}}
											InputLabelProps={{ shrink: false }}
											placeholder="Search here..."
										/>
									)}
								/>
							) : headCell.label === "Loan XML Upload Date" ? (
								<Box className={styles.datePickerWrapper}>
									<DatePicker
										selected={filters?.loan_application_date ? new Date(filters.loan_application_date) : null}
										onChange={(value) => onFiltersChange?.("loan_application_date", value)}
										placeholderText="Select date"
										dateFormat="yyyy-MM-dd"
										isClearable
										style={{ width: "100%" }}
									/>
									<CalendarMonthIcon className={styles.datePickerIcon} fontSize="small" />
								</Box>
							) : headCell.label === "Loan Submission Date" ? (
								<Box className={styles.datePickerWrapper}>
									<DatePicker
										selected={filters?.loan_application_completed_date ? new Date(filters.loan_application_completed_date) : null}
										onChange={(value) => onFiltersChange?.("loan_application_completed_date", value)}
										placeholderText="Select date"
										dateFormat="yyyy-MM-dd"
										isClearable
										style={{ width: "100%" }}
									/>
									<CalendarMonthIcon className={styles.datePickerIcon} fontSize="small" />
								</Box>
							) : headCell.id === "direct_path" ? (
								<Box className={styles.inputField} />
							) : (
								<OutlinedTextInput
									extraClass={styles.inputField}
									placeholder="Search here..."
									name={
										headCell.label === "Borrower Name"
											? "borrower_name"
											: headCell.label === "Go To Loan"
												? "application_number"
												: ""
									}
									value={
										headCell.label === "Borrower Name"
											? filters?.borrower_name || ""
											: headCell.label === "Go To Loan"
												? filters?.application_number || ""
												: ""
									}
									handleChange={(event) =>
										headCell.label === "Borrower Name"
											? onFiltersChange?.(event.target.name, event.target.value)
											: headCell.label === "Go To Loan" &&
												onFiltersChange?.(event.target.name, event.target.value)
									}
								/>
							)}
						</Stack>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
};

export default TableHeadUploadHistory;
